import React, { useEffect, useState } from 'react';
import axiosInstance from '../../config/axiosInstance';
import ManageModal from '../components/ManageModal';
import FundPasswordModal from '../components/FundPasswordModal';
import LoginPasswordModal from '../components/LoginPasswordModal';
import useResponsivePageSize from '../hooks/useResponsivePageSize';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = useResponsivePageSize();
  const [showFundPasswordModal, setShowFundPasswordModal] = useState(false);
  const [fundPasswordData, setFundPasswordData] = useState(null);
  const [loadingFundPassword, setLoadingFundPassword] = useState(false);
  const [loginPasswordUser, setLoginPasswordUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/users');
      if (response.data.success && response.data.data) {
        console.log(response.data.data.users);
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.uid?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.fullName?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(totalPages, 1)));
  }, [totalPages]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to generate pagination numbers with ellipsis
  const getPaginationNumbers = () => {
    const pages = [];

    if (totalPages <= 3) {
      // Show all pages if 3 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Check if we're in the last 3 pages
      if (currentPage >= totalPages - 2) {
        // Show last 3 pages without ellipsis (e.g., 6,7,8)
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show current page and next page (2 consecutive)
        pages.push(currentPage);
        pages.push(currentPage + 1);
        // Add ellipsis
        pages.push('...');
        // Add last 2 pages
        pages.push(totalPages - 1);
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const openModal = (user) => {
    setSelectedUser(user);
  };

  const handleUpdateUser = (updatedUser) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? updatedUser : u))
    );
    setSelectedUser(updatedUser);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  const handleOpenFundPasswordModal = async (user) => {
    setShowFundPasswordModal(true);
    setFundPasswordData(null);
    setLoadingFundPassword(true);

    try {
      const response = await axiosInstance.get(
        `/api/admin/transaction-passwords?uid=${user.uid}`
      );
      if (response.data.success && response.data.data) {
        setFundPasswordData(response.data.data.user);
      }
    } catch (error) {
      console.error('Error fetching fund password:', error);
    } finally {
      setLoadingFundPassword(false);
    }
  };

  const handleCloseFundPasswordModal = () => {
    setShowFundPasswordModal(false);
    setFundPasswordData(null);
  };

  const handleUpdateFundPassword = async (newPassword) => {
    try {
      const response = await axiosInstance.put(
        `/api/admin/transaction-passwords/${fundPasswordData.uid}`,
        {
          newPassword: newPassword,
        }
      );

      if (response.data.success) {
        // Refresh the fund password data
        const refreshResponse = await axiosInstance.get(
          `/api/admin/transaction-passwords?uid=${fundPasswordData.uid}`
        );
        if (refreshResponse.data.success) {
          setFundPasswordData(refreshResponse.data.data.user);
        }
        return { success: true, message: 'Fund password updated successfully' };
      }
    } catch (error) {
      console.error('Error updating fund password:', error);
      return {
        success: false,
        message:
          error.response?.data?.message || 'Failed to update fund password',
      };
    }
  };

  return (
    <div className='container-fluid admin-users-page px-2 px-sm-3'>
      <div className='card admin-users-card shadow-sm border-0'>
        <div className='card-header bg-white border-bottom admin-users-card__header'>
          <h5 className='mb-0 fw-semibold'>Registered Users</h5>
        </div>
        <div className='card-body p-0'>
          {/* Search Input */}
          <div className='admin-users-search'>
            <input
              className='form-control form-control-sm'
              placeholder='Search by UID, Email or Name'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className='text-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className='table-responsive'>
                <table className='table table-hover align-middle mb-0 admin-users-table'>
                  <thead className='table-light'>
                    <tr>
                      <th className='text-start ps-3'>UID</th>
                      <th className='text-start'>Email</th>
                      <th className='text-start'>Name</th>
                      <th className='text-start'>Status</th>
                      <th className='text-start'>KYC Status</th>
                      <th className='text-start'>Joined</th>
                      <th className='text-start'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.length > 0 ? (
                      currentUsers.map((user) => (
                        <tr
                          key={user.id}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td className='text-start ps-3'>{user.uid}</td>
                          <td className='text-start'>{user.email}</td>
                          <td className='text-start'>
                            {user.fullName || 'N/A'}
                          </td>
                          <td className='text-start'>
                            <span
                              className={`badge ${
                                user.status === 'ACTIVE'
                                  ? 'bg-success'
                                  : 'bg-danger'
                              }`}
                            >
                              {user.status}
                            </span>
                          </td>
                          <td className='text-start'>
                            <span
                              className={`badge ${
                                user.kycStatus === 'APPROVED'
                                  ? 'bg-success'
                                  : user.kycStatus === 'PENDING'
                                  ? 'bg-warning'
                                  : 'bg-danger'
                              }`}
                            >
                              {user.kycStatus}
                            </span>
                          </td>
                          <td className='text-start'>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td className='text-start'>
                            <button
                              className='btn btn-sm btn-primary rounded-pill px-2 admin-users-table__manage'
                              onClick={() => openModal(user)}
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan='7' className='text-center py-5 text-muted'>
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        {/* Pagination */}
        {!loading && filteredUsers.length > itemsPerPage && (
          <div className='card-footer bg-white border-top admin-users-card__footer'>
            <div className='d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2'>
              <div className='text-muted small text-center text-sm-start'>
                Showing {indexOfFirstItem + 1} to{' '}
                {Math.min(indexOfLastItem, filteredUsers.length)} of{' '}
                {filteredUsers.length} users
              </div>
              <nav>
                <ul className='pagination pagination-sm mb-0'>
                  <li
                    className={`page-item ${
                      currentPage === 1 ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>
                  </li>
                  {getPaginationNumbers().map((page, index) => {
                    if (page === '...') {
                      return (
                        <li
                          key={`ellipsis-${index}`}
                          className='page-item disabled'
                        >
                          <span className='page-link'>...</span>
                        </li>
                      );
                    }
                    return (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page ? 'active' : ''
                        }`}
                      >
                        <button
                          className='page-link'
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      <ManageModal
        selectedUser={selectedUser}
        onClose={handleCloseModal}
        onUpdate={handleUpdateUser}
        onChangeLoginPassword={setLoginPasswordUser}
        onOpenFundPassword={handleOpenFundPasswordModal}
      />

      <LoginPasswordModal
        user={loginPasswordUser}
        onClose={() => setLoginPasswordUser(null)}
      />

      {/* Fund Password Modal */}
      <FundPasswordModal
        isOpen={showFundPasswordModal}
        onClose={handleCloseFundPasswordModal}
        fundPasswordData={fundPasswordData}
        loading={loadingFundPassword}
        onUpdate={handleUpdateFundPassword}
      />
    </div>
  );
};

export default AdminUsers;
